import Contact from '../../models/Contact';

export type ProfileUpdatePayload = Partial<{
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  reauthCode?: string;
  mobileVerificationCode?: string;
}>;

export type ProfileUpdateResult = Contact & {
  emailChangePending?: boolean;
  pendingEmail?: string;
  emailChangeExpiry?: string;
  mobileChangePending?: boolean;
  pendingMobile?: string;
  mobileVerificationExpiry?: number;
};

export type OwnContactResult = Contact & {
  pendingEmail?: string;
  emailChangeExpiry?: string;
  pendingMobile?: string;
  mobileVerificationExpiry?: number;
};

export type ChangePasswordResult = {
  success: boolean;
  message: string;
  expiresAt?: number;
  error?: string;
  code?: string;
  reAuthUrl?: string;
  reauthCode?: string;
};

type StructuredReauthError = {
  code: string;
  error: string;
  structuredError: false;
  reAuthUrl?: string;
  reauthCode?: string;
  message?: string;
  changedFields?: string[];
};

export function getProfileUpdateReauthError(
  response: Response,
  errorBody: Record<string, unknown>,
): StructuredReauthError | null {
  if (response.status === 401 && errorBody.code === 'REAUTH_REQUIRED') {
    return {
      code: 'REAUTH_REQUIRED',
      error: (errorBody.error as string) || 'Re-authentication required',
      reAuthUrl: errorBody.reAuthUrl as string | undefined,
      reauthCode: errorBody.reauthCode as string | undefined,
      message: errorBody.message as string | undefined,
      changedFields: errorBody.changedFields as string[] | undefined,
      structuredError: false,
    };
  }
  if (response.status === 403 && errorBody.code === 'REAUTH_USER_MISMATCH') {
    return {
      code: 'REAUTH_USER_MISMATCH',
      error: (errorBody.error as string) || 'Re-authentication failed: User mismatch',
      structuredError: false,
    };
  }
  if (
    response.status === 401 &&
    (errorBody.code === 'REAUTH_REQUIRED' || errorBody.code === 'REAUTH_TOKEN_EXPIRED')
  ) {
    return {
      code: errorBody.code as string,
      error: (errorBody.error as string) || 'Re-authentication required',
      message: errorBody.message as string | undefined,
      structuredError: false,
    };
  }
  return null;
}

export function mapProfileUpdateResponse(json: Record<string, unknown>): ProfileUpdateResult {
  return {
    ...(json.data as Contact),
    emailChangePending: (json.emailChangePending as boolean) || false,
    pendingEmail: json.pendingEmail as string | undefined,
    emailChangeExpiry: json.emailChangeExpiry as string | undefined,
    mobileChangePending: (json.mobileChangePending as boolean) || false,
    pendingMobile: json.pendingMobile as string | undefined,
    mobileVerificationExpiry: json.mobileVerificationExpiry as number | undefined,
  };
}

export function rethrowOrWrapProfileUpdateError(
  error: unknown,
  handleError: (body: { code: string; error: string }) => unknown,
): never {
  if (error && typeof error === 'object' && 'code' in error && 'error' in error) {
    throw error;
  }
  if (typeof error === 'string') {
    throw error;
  }
  if (
    error &&
    typeof error === 'object' &&
    'structuredError' in error &&
    !(error as { structuredError: boolean }).structuredError
  ) {
    throw error;
  }
  throw handleError({
    code: 'NETWORK_ERROR',
    error: 'Network error while updating profile',
  });
}

export function parseChangePasswordResponse(
  response: Response,
  data: Record<string, unknown>,
): ChangePasswordResult {
  if (!response.ok) {
    return {
      success: false,
      message: (data.error as string) || 'Failed to initiate password change',
      error: data.error as string | undefined,
      code: data.code as string | undefined,
      reAuthUrl: data.reAuthUrl as string | undefined,
      reauthCode: data.reauthCode as string | undefined,
    };
  }
  return {
    success: true,
    message: (data.message as string) || 'Password change link has been sent to your email address',
    expiresAt: data.expiresAt as number | undefined,
  };
}

export function rethrowOrWrapChangePasswordError(
  error: unknown,
  handleError: (body: { code: string; error: string }) => unknown,
): never {
  if (typeof error === 'string') {
    throw error;
  }
  if (error && typeof error === 'object' && 'code' in error && 'error' in error) {
    throw error;
  }
  throw handleError({
    code: 'NETWORK_ERROR',
    error: 'Network error while initiating password change',
  });
}

export function mapOwnContactResponse(json: Record<string, unknown>): OwnContactResult {
  const data = json.data as Contact & OwnContactResult;
  return {
    ...data,
    pendingEmail: data.pendingEmail,
    emailChangeExpiry: data.emailChangeExpiry,
    pendingMobile: data.pendingMobile,
    mobileVerificationExpiry: data.mobileVerificationExpiry,
  };
}
